import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Loader2, CheckCircle, Clock, AlertTriangle,
  User, Calendar, FileText, Phone, Search, Mail, Scale, Briefcase,
  MoreHorizontal, RefreshCw, AlertCircle as AlertIcon, Filter, 
  ChevronDown, ChevronRight, Play, BookTemplate, Sparkles, ListChecks
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority configuration
const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700', icon: null },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700', icon: null },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  emergency: { label: 'Emergency', color: 'bg-red-100 text-red-700', icon: AlertIcon }
};

// Task type icons
const taskTypeIcons = {
  draft_document: FileText,
  research: Search,
  client_call: Phone,
  client_meeting: User,
  court_filing: Scale,
  review_document: FileText,
  send_correspondence: Mail,
  prepare_discovery: Briefcase,
  deposition_prep: User,
  trial_prep: Scale,
  administrative: Briefcase,
  follow_up: RefreshCw,
  other: MoreHorizontal
};

export default function CaseTasksTab({ caseId, caseData }) {
  const { token } = useAuth();
  
  // State
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState({ system_statuses: [], custom_statuses: [] });
  const [taskTypes, setTaskTypes] = useState({ system_types: [], custom_types: [] });
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Dialog state
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Complete dialog state
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    actual_hours: '',
    completion_notes: '',
    log_time_entry: false
  });
  
  // Template state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templates, setTemplates] = useState({ system_templates: [], custom_templates: [], categories: [] });
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  const [templateAssignees, setTemplateAssignees] = useState([]);
  const [templateDueDate, setTemplateDueDate] = useState('');
  
  // Form state
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    task_type: 'other',
    priority: 'medium',
    status: 'not_started',
    due_date: '',
    due_time: '',
    assignee_user_ids: [],
    estimated_hours: '',
    reminders: [{ enabled: true, time_before: 1, unit: 'days', notify_email: true, notify_app: true }],
    notes: ''
  });

  // Fetch data
  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (!showCompleted) params.append('include_completed', 'false');
      
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/tasks?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTasks(await res.json());
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [caseId, token, showCompleted]);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/task-statuses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setStatuses(await res.json());
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  }, [token]);

  const fetchTaskTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/task-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTaskTypes(await res.json());
    } catch (error) {
      console.error('Error fetching task types:', error);
    }
  }, [token]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data.members || []);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTasks(),
        fetchStatuses(),
        fetchTaskTypes(),
        fetchTeamMembers()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchTasks, fetchStatuses, fetchTaskTypes, fetchTeamMembers]);

  // Reset form
  const resetForm = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 7);
    
    setTaskForm({
      title: '',
      description: '',
      task_type: 'other',
      priority: 'medium',
      status: 'not_started',
      due_date: tomorrow.toISOString().split('T')[0],
      due_time: '',
      assignee_user_ids: [],
      estimated_hours: '',
      reminders: [{ enabled: true, time_before: 1, unit: 'days', notify_email: true, notify_app: true }],
      notes: ''
    });
    setEditingTask(null);
  };

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const caseType = caseData?.case_type || '';
      const res = await fetch(`${API_URL}/api/case-management/task-templates?case_type=${caseType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, [token, caseData?.case_type]);

  // Open template dialog
  const handleOpenTemplates = () => {
    fetchTemplates();
    setShowTemplateDialog(true);
    setSelectedCategory('all');
    setApplyingTemplate(null);
    setTemplateAssignees([]);
    
    // Set default due date
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 7);
    setTemplateDueDate(defaultDue.toISOString().split('T')[0]);
  };

  // Apply template
  const handleApplyTemplate = async (template) => {
    setApplyingTemplate(template);
    
    // Calculate due date from template
    if (template.default_due_days) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + template.default_due_days);
      setTemplateDueDate(dueDate.toISOString().split('T')[0]);
    }
  };

  // Confirm template application
  const handleConfirmTemplate = async () => {
    if (!applyingTemplate) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('assignee_user_ids', JSON.stringify(templateAssignees));
      if (templateDueDate) {
        formData.append('due_date_override', templateDueDate);
      }

      const res = await fetch(`${API_URL}/api/case-management/task-templates/${applyingTemplate.id}/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(`Task "${applyingTemplate.name}" created from template`);
        setShowTemplateDialog(false);
        setApplyingTemplate(null);
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to apply template');
      }
    } catch (error) {
      toast.error('Failed to apply template');
    } finally {
      setSaving(false);
    }
  };

  // Toggle template assignee
  const toggleTemplateAssignee = (userId) => {
    setTemplateAssignees(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Get filtered templates
  const getFilteredTemplates = () => {
    const all = [...templates.system_templates, ...templates.custom_templates];
    if (selectedCategory === 'all') return all;
    return all.filter(t => t.category === selectedCategory);
  };

  // Open create dialog
  const handleCreate = () => {
    resetForm();
    setShowTaskDialog(true);
  };

  // Open edit dialog
  const handleEdit = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      task_type: task.task_type,
      priority: task.priority,
      status: task.status,
      due_date: task.due_date || '',
      due_time: task.due_time || '',
      assignee_user_ids: task.assignees?.map(a => a.user_id) || [],
      estimated_hours: task.estimated_hours || '',
      reminders: task.reminders || [],
      notes: task.notes || ''
    });
    setShowTaskDialog(true);
  };

  // Save task
  const handleSave = async () => {
    if (!taskForm.title) {
      toast.error('Please enter a task title');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', taskForm.title);
      formData.append('description', taskForm.description);
      formData.append('task_type', taskForm.task_type);
      formData.append('priority', taskForm.priority);
      formData.append('status', taskForm.status);
      formData.append('due_date', taskForm.due_date);
      formData.append('due_time', taskForm.due_time);
      formData.append('assignee_user_ids', JSON.stringify(taskForm.assignee_user_ids));
      formData.append('estimated_hours', taskForm.estimated_hours || '');
      formData.append('reminders', JSON.stringify(taskForm.reminders));
      formData.append('notes', taskForm.notes);

      const url = editingTask 
        ? `${API_URL}/api/case-management/cases/${caseId}/tasks/${editingTask.id}`
        : `${API_URL}/api/case-management/cases/${caseId}/tasks`;
      
      const res = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(editingTask ? 'Task updated' : 'Task created');
        setShowTaskDialog(false);
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save task');
      }
    } catch (error) {
      toast.error('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  // Delete task
  const handleDelete = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Task deleted');
        fetchTasks();
      } else {
        toast.error('Failed to delete task');
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Open complete dialog
  const handleOpenComplete = (task) => {
    setCompletingTask(task);
    setCompleteForm({
      actual_hours: task.estimated_hours || '',
      completion_notes: '',
      log_time_entry: false
    });
    setShowCompleteDialog(true);
  };

  // Complete task
  const handleComplete = async () => {
    if (!completingTask) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      if (completeForm.actual_hours) formData.append('actual_hours', completeForm.actual_hours);
      if (completeForm.completion_notes) formData.append('completion_notes', completeForm.completion_notes);
      formData.append('log_time_entry', completeForm.log_time_entry);

      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/tasks/${completingTask.id}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Task completed');
        if (data.time_entry_id) {
          toast.success('Time entry logged');
        }
        setShowCompleteDialog(false);
        fetchTasks();
      } else {
        toast.error('Failed to complete task');
      }
    } catch (error) {
      toast.error('Failed to complete task');
    } finally {
      setSaving(false);
    }
  };

  // Quick status change
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);

      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Status updated');
        fetchTasks();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Toggle assignee
  const toggleAssignee = (userId) => {
    setTaskForm(prev => ({
      ...prev,
      assignee_user_ids: prev.assignee_user_ids.includes(userId)
        ? prev.assignee_user_ids.filter(id => id !== userId)
        : [...prev.assignee_user_ids, userId]
    }));
  };

  // Get status info
  const getStatusInfo = (statusId) => {
    const all = [...statuses.system_statuses, ...statuses.custom_statuses];
    return all.find(s => s.id === statusId) || { name: statusId, color: '#6b7280' };
  };

  // Check if overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date(new Date().toDateString());
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    return true;
  });

  // Get task type info
  const getTaskTypeInfo = (typeId) => {
    const all = [...(taskTypes.system_types || []), ...(taskTypes.custom_types || [])];
    return all.find(t => t.id === typeId) || { name: typeId, icon: 'MoreHorizontal', color: '#6b7280' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const allStatuses = [...statuses.system_statuses, ...statuses.custom_statuses];

  return (
    <div className="space-y-6" data-testid="case-tasks-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Tasks</h2>
          <p className="text-sm text-slate-500">
            {tasks.filter(t => !['completed', 'cancelled'].includes(t.status)).length} active tasks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleOpenTemplates} data-testid="from-template-btn">
            <BookTemplate className="w-4 h-4 mr-1" />
            From Template
          </Button>
          <Button onClick={handleCreate} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {allStatuses.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="emergency">Emergency</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Checkbox
            id="show-completed"
            checked={showCompleted}
            onCheckedChange={(checked) => {
              setShowCompleted(checked);
            }}
          />
          <Label htmlFor="show-completed" className="cursor-pointer text-sm">
            Show completed
          </Label>
        </div>
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-medium text-slate-700">No tasks found</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Create tasks to track work for this case
              </p>
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-1" />
                Add First Task
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map(task => {
            const statusInfo = getStatusInfo(task.status);
            const typeInfo = getTaskTypeInfo(task.task_type);
            const TaskIcon = taskTypeIcons[task.task_type] || MoreHorizontal;
            const PriorityIcon = priorityConfig[task.priority]?.icon;
            const overdue = isOverdue(task.due_date) && !['completed', 'cancelled'].includes(task.status);

            return (
              <Card 
                key={task.id} 
                className={`hover:shadow-md transition-shadow ${overdue ? 'border-red-300 bg-red-50/30' : ''} ${task.status === 'completed' ? 'opacity-60' : ''}`}
                data-testid={`task-${task.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Task Type Icon */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${typeInfo.color}20` }}
                    >
                      <TaskIcon className="w-5 h-5" style={{ color: typeInfo.color }} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                              {task.title}
                            </h4>
                            <Badge className={priorityConfig[task.priority]?.color}>
                              {PriorityIcon && <PriorityIcon className="w-3 h-3 mr-1" />}
                              {priorityConfig[task.priority]?.label}
                            </Badge>
                            {overdue && (
                              <Badge className="bg-red-100 text-red-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Overdue
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            {task.due_date && (
                              <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(task.due_date).toLocaleDateString()}
                                {task.due_time && ` at ${task.due_time}`}
                              </span>
                            )}
                            {task.assignees?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                {task.assignees.map(a => a.name).join(', ')}
                              </span>
                            )}
                            {task.estimated_hours && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {task.estimated_hours}h est.
                              </span>
                            )}
                          </div>
                          
                          {task.description && (
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{task.description}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Status Dropdown */}
                          <Select 
                            value={task.status} 
                            onValueChange={(v) => handleStatusChange(task.id, v)}
                          >
                            <SelectTrigger className="w-36 h-8 text-xs">
                              <div className="flex items-center gap-1">
                                <span 
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: statusInfo.color }}
                                />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {allStatuses.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                  <span className="flex items-center gap-2">
                                    <span 
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: s.color }}
                                    />
                                    {s.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {/* Actions */}
                          {task.status !== 'completed' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleOpenComplete(task)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(task)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(task.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create/Edit Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Update task details' : 'Create a new task for this case'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Draft Motion for Summary Judgment"
                data-testid="task-title-input"
              />
            </div>

            {/* Type and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Task Type</Label>
                <Select
                  value={taskForm.task_type}
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, task_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taskTypes.system_types?.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                    {taskTypes.custom_types?.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <Select
                value={taskForm.status}
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allStatuses.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        <span 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: s.color }}
                        />
                        {s.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="task-due-date"
                />
              </div>
              <div>
                <Label htmlFor="due_time">Due Time (optional)</Label>
                <Input
                  id="due_time"
                  type="time"
                  value={taskForm.due_time}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_time: e.target.value }))}
                />
              </div>
            </div>

            {/* Estimated Hours */}
            <div>
              <Label htmlFor="estimated_hours">Estimated Hours</Label>
              <Input
                id="estimated_hours"
                type="number"
                step="0.25"
                value={taskForm.estimated_hours}
                onChange={(e) => setTaskForm(prev => ({ ...prev, estimated_hours: e.target.value }))}
                placeholder="e.g., 2.5"
              />
            </div>

            {/* Assignees */}
            {teamMembers.length > 0 && (
              <div>
                <Label>Assign To</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-lg max-h-32 overflow-y-auto">
                  {teamMembers.map(member => (
                    <div key={member.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`assignee-${member.id}`}
                        checked={taskForm.assignee_user_ids.includes(member.id)}
                        onCheckedChange={() => toggleAssignee(member.id)}
                      />
                      <Label htmlFor={`assignee-${member.id}`} className="cursor-pointer text-sm">
                        {member.name || member.email}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add details about this task..."
                rows={3}
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                value={taskForm.notes}
                onChange={(e) => setTaskForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any internal notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-task-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                editingTask ? 'Update Task' : 'Create Task'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Task Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Complete Task
            </DialogTitle>
            <DialogDescription>
              Mark "{completingTask?.title}" as completed
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="actual_hours">Actual Hours Spent</Label>
              <Input
                id="actual_hours"
                type="number"
                step="0.25"
                value={completeForm.actual_hours}
                onChange={(e) => setCompleteForm(prev => ({ ...prev, actual_hours: e.target.value }))}
                placeholder={completingTask?.estimated_hours ? `Estimated: ${completingTask.estimated_hours}h` : 'e.g., 2.5'}
              />
            </div>

            <div>
              <Label htmlFor="completion_notes">Completion Notes</Label>
              <Textarea
                id="completion_notes"
                value={completeForm.completion_notes}
                onChange={(e) => setCompleteForm(prev => ({ ...prev, completion_notes: e.target.value }))}
                placeholder="Add any notes about the completed work..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2 p-3 border rounded-lg bg-slate-50">
              <Checkbox
                id="log_time_entry"
                checked={completeForm.log_time_entry}
                onCheckedChange={(checked) => setCompleteForm(prev => ({ ...prev, log_time_entry: checked }))}
              />
              <Label htmlFor="log_time_entry" className="cursor-pointer">
                Log time entry for billing ({completeForm.actual_hours || '0'}h)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleComplete} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Complete Task
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5 text-indigo-600" />
              Task Templates
            </DialogTitle>
            <DialogDescription>
              Select a template to quickly create a task with pre-filled details
              {caseData?.case_type && (
                <span className="ml-1">
                  (showing templates for <span className="font-medium">{caseData.case_type.replace('_', ' ')}</span>)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingTemplates ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : applyingTemplate ? (
            // Template application form
            <div className="space-y-4 py-4">
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-indigo-900">{applyingTemplate.name}</h4>
                    <p className="text-sm text-indigo-700 mt-1">{applyingTemplate.description}</p>
                    {applyingTemplate.checklist?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-indigo-800 mb-1">Checklist included:</p>
                        <ul className="text-xs text-indigo-700 space-y-0.5">
                          {applyingTemplate.checklist.slice(0, 4).map((item, i) => (
                            <li key={i} className="flex items-center gap-1">
                              <ListChecks className="w-3 h-3" />
                              {item}
                            </li>
                          ))}
                          {applyingTemplate.checklist.length > 4 && (
                            <li className="text-indigo-500">...and {applyingTemplate.checklist.length - 4} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-indigo-600">
                      <span className="flex items-center gap-1">
                        <Badge variant="outline" className={priorityConfig[applyingTemplate.priority]?.color}>
                          {priorityConfig[applyingTemplate.priority]?.label}
                        </Badge>
                      </span>
                      {applyingTemplate.estimated_hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {applyingTemplate.estimated_hours}h est.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="template_due_date">Due Date</Label>
                <Input
                  id="template_due_date"
                  type="date"
                  value={templateDueDate}
                  onChange={(e) => setTemplateDueDate(e.target.value)}
                  className="mt-1"
                />
                {applyingTemplate.default_due_days && (
                  <p className="text-xs text-slate-500 mt-1">
                    Default: {applyingTemplate.default_due_days} days from today
                  </p>
                )}
              </div>

              {teamMembers.length > 0 && (
                <div>
                  <Label>Assign To</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-lg max-h-32 overflow-y-auto">
                    {teamMembers.map(member => (
                      <div key={member.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`tpl-assignee-${member.id}`}
                          checked={templateAssignees.includes(member.id)}
                          onCheckedChange={() => toggleTemplateAssignee(member.id)}
                        />
                        <Label htmlFor={`tpl-assignee-${member.id}`} className="cursor-pointer text-sm">
                          {member.name || member.email}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Template list
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Category filter */}
              <div className="flex items-center gap-2 pb-3 border-b">
                <span className="text-sm text-slate-500">Category:</span>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {templates.categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400 ml-auto">
                  {getFilteredTemplates().length} templates
                </span>
              </div>

              {/* Template grid */}
              <div className="flex-1 overflow-y-auto py-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getFilteredTemplates().map(template => (
                    <Card 
                      key={template.id} 
                      className="cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors"
                      onClick={() => handleApplyTemplate(template)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-slate-900 text-sm">{template.name}</h4>
                              {template.is_system && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-slate-100">
                                  System
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge variant="outline" className={`text-[10px] ${priorityConfig[template.priority]?.color}`}>
                                {priorityConfig[template.priority]?.label}
                              </Badge>
                              {template.category && (
                                <span className="text-[10px] text-slate-400">{template.category}</span>
                              )}
                              {template.estimated_hours && (
                                <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                  <Clock className="w-3 h-3" />
                                  {template.estimated_hours}h
                                </span>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-300 ml-2 flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {getFilteredTemplates().length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <BookTemplate className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>No templates found for this category</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            {applyingTemplate ? (
              <>
                <Button variant="outline" onClick={() => setApplyingTemplate(null)}>
                  Back to Templates
                </Button>
                <Button onClick={handleConfirmTemplate} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      Create Task
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
